/* eslint-disable react/prop-types */
import { useMemo } from 'react'
import { Font, View, Page, StyleSheet, Text, Document, Image } from '@react-pdf/renderer'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { ServiceExecutionUpdateRequest } from '../../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
import {
	TimeSheetPageState,
	ServiceOriginal,
	ServiceExecution,
} from '../../../../../store/slices/timesheet/timesheetPageSlice'
import { TimesheetStoreState } from '../../../../../store/timesheetStore'
import { useIsTimesheetApproved } from '../../../../../utils/hooks'
import { useParams } from 'react-router-dom'
import { devLog, devWarn } from '../../../../../utils/feedback'
import { mapImageCompanyUrlTo } from '../../../../v2/general/NavBar'

// Used to determine which font to use in styles (Poppins or Helvetica as fallback)
let poppinsRegistered = false
try {
	// Register the Poppins font family with all necessary variations
	Font.register({
		family: 'Poppins',
		fonts: [
			{
				src: '/fonts/Poppins-Regular.ttf',
				fontWeight: 400,
				fontStyle: 'normal',
			},
			{
				src: '/fonts/Poppins-Medium.ttf',
				fontWeight: 500,
				fontStyle: 'normal',
			},
			{
				src: '/fonts/Poppins-SemiBold.ttf',
				fontWeight: 600,
				fontStyle: 'normal',
			},
			{
				src: '/fonts/Poppins-Bold.ttf',
				fontWeight: 700,
				fontStyle: 'normal',
			},
			// Italic Versions
			{
				src: '/fonts/Poppins-Italic.ttf',
				fontWeight: 400,
				fontStyle: 'italic',
			},
			{
				src: '/fonts/Poppins-Italic.ttf',
				fontWeight: 500,
				fontStyle: 'italic',
			},
			{
				src: '/fonts/Poppins-Italic.ttf',
				fontWeight: 600,
				fontStyle: 'italic',
			},
			{
				src: '/fonts/Poppins-BoldItalic.ttf',
				fontWeight: 700,
				fontStyle: 'italic',
			},
		],
	})
	// Mark as registered only if there is no error
	poppinsRegistered = true
} catch (error) {
	// In case of error, log a warning and keep the flag as false
	// The code will automatically use Helvetica as a fallback
	devWarn(
		'Erro ao registrar fonte Poppins. Certifique-se de que os arquivos TTF estão em public/fonts/. Usando Helvetica como fallback.',
		error,
	)
}

// PDF Styles
const styles = StyleSheet.create({
	page: {
		padding: 30,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		fontSize: 10,
		backgroundColor: '#FFFFFF',
	},
	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginBottom: '16px',
		padding: '8px',
		paddingBottom: '8px',
		backgroundColor: '#F5F5F5',
		borderRadius: '5px',
	},
	headerLeft: {
		flex: 1,
	},
	motoristaNome: {
		fontSize: 12,
		fontWeight: 600,
		color: '#333333',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0,
	},
	motoristaInfo: {
		fontSize: 7,
		fontWeight: 400,
		color: '#000000',
		lineHeight: 1.3,
		letterSpacing: 0,
		margin: 0,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
	},
	headerRight: {
		textAlign: 'right',
	},
	data: {
		fontSize: 12,
		fontWeight: 600,
		color: '#333333',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0,
	},
	section: {
		marginBottom: '16px',
	},
	sectionTitle: {
		fontSize: 12,
		fontWeight: 600,
		color: '#000000',
		letterSpacing: 0,
		lineHeight: 1.3,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
	},
	table: {
		width: '100%',
		marginBottom: 2,
		backgroundColor: '#FFFFFF',
	},
	tableRow: {
		flexDirection: 'row',
		borderBottomWidth: 0.4,
		borderBottomColor: '#4D4D4D',
		backgroundColor: '#FFFFFF',
		paddingTop: 5,
		paddingBottom: 5,
	},
	tableHeader: {
		fontWeight: 600,
		fontSize: 7,
		paddingTop: 5,
		paddingBottom: 5,
		paddingLeft: 5,
		paddingRight: 5,
		textAlign: 'left',
		color: '#000000',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.38,
		letterSpacing: 0.07,
	},
	tableCell: {
		fontSize: 7,
		fontWeight: 400,
		color: '#000000',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0,
	},
	tableCellCenter: {
		fontSize: 7,
		fontWeight: 400,
		textAlign: 'center',
		color: '#000000',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0,
	},
	minHeightSection: {
		minHeight: 100,
	},
	footer: {
		position: 'absolute',
		bottom: 20,
		right: 30,
		fontSize: 6,
		fontWeight: 400,
		color: '#666666',
		textAlign: 'right',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		letterSpacing: 0,
	},
})

export const ServicePrint: React.FC<{
	timesheetPage: TimeSheetPageState
	serviceOriginal: ServiceOriginal
	serviceExecution: ServiceExecution
	serviceApproval: ServiceExecutionUpdateRequest
}> = ({ timesheetPage, serviceApproval, serviceExecution, serviceOriginal }) => {
	const params = useParams<{ id?: string; serviceId?: string }>()
	const storedCompany = localStorage.getItem('Company')
	const targetServiceId = serviceOriginal?.serviceId ?? params.serviceId
	devLog('Service Execution:', serviceExecution)
	const calculateKms = (service: ServiceOriginal | ServiceExecution) => {
		return service?.kmsVehicles?.reduce((acc, curr) => acc + curr.kmsEnd - curr.kmsIni, 0) || 0
	}

	const service = useMemo(() => {
		if (targetServiceId !== null && targetServiceId !== undefined && targetServiceId !== '') {
			return timesheetPage.services.find(s => s.service?.serviceId === Number(targetServiceId))
		}
	}, [targetServiceId, timesheetPage.services])

	const coments = useMemo(() => {
		const targetService = timesheetPage.services.find(s => s.service?.serviceId === Number(targetServiceId))
		return targetService?.serviceExecution?.comments?.trim() ?? ''
	}, [timesheetPage.services, targetServiceId])

	const kms = useMemo(() => {
		if (timesheetPage?.isSubmitted) {
			return service?.serviceExecution?.kmsVehicles
		}
		return service?.service?.kmsVehicles
	}, [service, timesheetPage?.isSubmitted])

	const isServiceConforme = (service: ServiceOriginal, serviceExecution?: ServiceExecution): boolean => {
		if (!serviceExecution) {
			return true
		}

		// Comparing startDateTime - they should be the same
		const serviceStart = dayjs(service.startDateTime)
		const executionStart = dayjs(serviceExecution?.startDateTime)

		if (!serviceStart.isValid() || !executionStart.isValid()) {
			return false
		}

		// startDateTime should be the same
		if (!serviceStart.isSame(executionStart, 'minute')) {
			return false
		}

		// Comparing endDateTime - serviceExecution can be at most 1 hour later
		const serviceEnd = dayjs(service.endDateTime)
		const executionEnd = dayjs(serviceExecution.endDateTime)

		if (!serviceEnd.isValid() || !executionEnd.isValid()) {
			return false
		}

		// endDateTime of serviceExecution cannot be earlier than service
		if (executionEnd.isBefore(serviceEnd)) {
			return false
		}

		// endDateTime of serviceExecution can be at most 1 hour later
		const diffInHours = executionEnd.diff(serviceEnd, 'hour', true)
		if (diffInHours > 1) {
			return false
		}

		return true
	}

	const calculateKmsByMode = (service: ServiceOriginal | ServiceExecution, inEmpty: boolean) => {
		if (service && 'kmsFull' in service && 'kmsEmpty' in service) {
			// ServiceOriginal has kmsFull and kmsEmpty
			return inEmpty ? (service as ServiceOriginal).kmsEmpty || 0 : (service as ServiceOriginal).kmsFull || 0
		}
		// ServiceExecution - calculate from kmsVehicles
		const vehicles = service?.kmsVehicles || []
		return vehicles.reduce((acc, kv) => {
			if (kv.inEmpty === inEmpty) {
				return acc + (kv.kmsEnd - kv.kmsIni)
			}
			return acc
		}, 0)
	}

	const calculateKmsTotal = (service: ServiceOriginal | ServiceExecution) => {
		if (service && 'kmsFull' in service && 'kmsEmpty' in service) {
			const s = service as ServiceOriginal
			return (s.kmsFull || 0) + (s.kmsEmpty || 0)
		}
		return calculateKms(service)
	}

	return (
		<Document>
			<Page
				size='A4'
				style={styles.page}
				wrap
			>
				{/* Header */}
				<View style={styles.header}>
					<View style={styles.headerLeft}>
						<Text style={styles.motoristaNome}>{timesheetPage?.driver?.driverName || 'N/A'}</Text>
						<Text style={styles.motoristaInfo}>
							Motorista nr {timesheetPage?.driver?.internalNumber || 'N/A'} -{' '}
							{timesheetPage?.driver?.companyName || 'N/A'}
						</Text>
					</View>
					<View style={styles.headerRight}>
						<Text style={styles.data}>{dayjs(timesheetPage?.timeSheetDate).format('DD/MM/YYYY')}</Text>
					</View>
				</View>

				{/* Services Section */}
				<View style={[styles.section, styles.minHeightSection]}>
					<Text style={styles.sectionTitle}>Serviço</Text>
					<View style={styles.table}>
						{/* Table Header */}
						<View
							style={styles.tableRow}
							wrap={false}
						>
							<Text style={[styles.tableHeader, { flex: 4.5 }]}>Início e Término (Escalado)</Text>
							<Text style={[styles.tableHeader, { flex: 2 }]}>
								Hora e Data Início
								{'\n'}
								Hora e Data Fim
							</Text>
							<Text style={[styles.tableHeader, { flex: 1.5, textAlign: 'left' }]}>Conforme</Text>
							<Text style={[styles.tableHeader, { flex: 1, textAlign: 'left' }]}>Kms Total</Text>
							<Text style={[styles.tableHeader, { flex: 1.5, textAlign: 'left' }]}>
								Kms Carga
								{'\n'}
								Kms Vazio
							</Text>
							<Text style={[styles.tableHeader, { flex: 1.5, textAlign: 'left' }]}>Passageiros</Text>
							<Text style={[styles.tableHeader, { flex: 1, textAlign: 'left' }]}>Limpeza</Text>
						</View>
						{/* Table Body */}
						<View
							style={styles.tableRow}
							wrap={false}
						>
							<View
								style={[
									{
										flex: 4.5,
										paddingTop: 5,
										paddingBottom: 5,
										paddingLeft: 5,
										paddingRight: 5,
										backgroundColor: '#FFFFFF',
										opacity: 0.65,
										display: 'flex',
										flexDirection: 'row',
									},
								]}
							>
								<View
									style={[
										styles.tableCell,
										{ display: 'flex', flexDirection: 'column', gap: '3px', justifyContent: 'space-between' },
									]}
								>
									<View style={{ display: 'flex', flexDirection: 'row', gap: '3px' }}>
										<Text
											wrap={true}
											style={{
												width: '90px',
												marginRight: '4px',
											}}
										>
											{serviceOriginal?.origin}
										</Text>
										<Text
											wrap={false}
											style={{ marginRight: '4px' }}
										>
											{dayjs(serviceOriginal?.startDateTime).format('HH:mm')}
										</Text>
										<Text wrap={false}>{dayjs(serviceOriginal?.startDateTime).format('DD/MM/YYYY')}</Text>
									</View>
									<View style={{ display: 'flex', flexDirection: 'row', gap: '3px' }}>
										<Text
											wrap={true}
											style={{
												width: '90px',
												marginRight: '4px',
											}}
										>
											{serviceOriginal?.destination}
										</Text>
										<Text
											wrap={false}
											style={{ marginRight: '4px' }}
										>
											{dayjs(serviceOriginal?.endDateTime).format('HH:mm')}
										</Text>
										<Text wrap={false}>{dayjs(serviceOriginal?.endDateTime).format('DD/MM/YYYY')}</Text>
									</View>
								</View>
							</View>
							<View
								style={[
									{
										flex: 2,
										paddingTop: 5,
										paddingBottom: 5,
										paddingLeft: 5,
										paddingRight: 5,
										backgroundColor: '#FFFFFF',
										display: 'flex',
										flexDirection: 'column',
										gap: '3px',
									},
								]}
							>
								<Text
									style={styles.tableCell}
									wrap={false}
								>
									{dayjs(
										timesheetPage?.isSubmitted
											? service?.serviceExecution?.startDateTime
											: service?.service?.startDateTime,
									).format('HH:mm DD/MM/YYYY')}
								</Text>
								<Text
									style={styles.tableCell}
									wrap={false}
								>
									{dayjs(
										timesheetPage?.isSubmitted ? service?.serviceExecution?.endDateTime : service?.service?.endDateTime,
									).format('HH:mm DD/MM/YYYY')}
								</Text>
							</View>
							<View
								style={[
									{
										flex: 1.5,
										alignItems: 'flex-start',
										paddingTop: 5,
										paddingBottom: 5,
										paddingLeft: 5,
										paddingRight: 5,
										backgroundColor: '#FFFFFF',
									},
								]}
							>
								<Text style={styles.tableCellCenter}>
									{isServiceConforme(serviceOriginal, serviceExecution) ? 'Sim' : 'Não'}
								</Text>
							</View>
							<View
								style={[
									{
										flex: 1,
										alignItems: 'flex-start',
										paddingTop: 5,
										paddingBottom: 5,
										paddingLeft: 5,
										paddingRight: 5,
										backgroundColor: '#FFFFFF',
									},
								]}
							>
								<Text style={styles.tableCellCenter}>
									{service &&
										(timesheetPage?.isSubmitted
											? calculateKmsTotal(service.serviceExecution as ServiceExecution)
											: calculateKmsTotal(service.service as ServiceOriginal))}
								</Text>
							</View>
							<View
								style={[
									{
										flex: 1.5,
										alignItems: 'flex-start',
										gap: '3px',
										paddingTop: 5,
										paddingBottom: 5,
										paddingLeft: 5,
										paddingRight: 5,
										backgroundColor: '#FFFFFF',
									},
								]}
							>
								<Text style={styles.tableCell}>
									{service &&
										(timesheetPage?.isSubmitted
											? calculateKmsByMode(service.serviceExecution as ServiceExecution, false)
											: calculateKmsByMode(service.service as ServiceOriginal, false))}
								</Text>
								<Text style={styles.tableCell}>
									{service &&
										(timesheetPage?.isSubmitted
											? calculateKmsByMode(service.serviceExecution as ServiceExecution, true)
											: calculateKmsByMode(service.service as ServiceOriginal, true))}
								</Text>
							</View>
							<View
								style={[
									{
										flex: 1.5,
										alignItems: 'flex-start',
										paddingTop: 5,
										paddingBottom: 5,
										paddingLeft: 5,
										paddingRight: 5,
										backgroundColor: '#FFFFFF',
									},
								]}
							>
								<Text style={styles.tableCellCenter}>
									{service &&
										(timesheetPage?.isSubmitted ? service.serviceExecution?.pax ?? 0 : service.service?.pax ?? 0)}
								</Text>
							</View>
							<View
								style={[
									{
										flex: 1,
										alignItems: 'flex-start',
										paddingTop: 5,
										paddingBottom: 5,
										paddingLeft: 5,
										paddingRight: 5,
										backgroundColor: '#FFFFFF',
									},
								]}
							>
								<Text style={styles.tableCellCenter}>
									{service &&
										(timesheetPage?.isSubmitted
											? service.serviceExecution?.isClean
												? 'Sim'
												: 'Não'
											: service.service?.isClean
												? 'Sim'
												: 'Não')}
								</Text>
							</View>
						</View>
					</View>
				</View>

				{/* Client Section */}
				<View style={styles.section}>
					<View style={[styles.table]}>
						{/* Table Header */}
						<View
							style={styles.tableRow}
							wrap={false}
						>
							<Text style={[styles.tableHeader, { flex: 7 }]}>Cliente</Text>
							<Text style={[styles.tableHeader, { flex: 1 }]}>ID Serviço</Text>
							<Text style={[styles.tableHeader, { flex: 1 }]}>Viatura</Text>
							<Text style={[styles.tableHeader, { flex: 3 }]}>{'Passageiros (Escalados)'}</Text>
						</View>
						{/* Table Rows */}
						<View
							style={styles.tableRow}
							wrap={false}
						>
							<Text style={[styles.tableCell, { flex: 7, paddingLeft: 7, paddingRight: 7 }]}>
								{serviceOriginal?.clientName}
							</Text>
							<Text style={[styles.tableCell, { flex: 1, paddingLeft: 5, paddingRight: 5 }]}>
								{serviceOriginal?.serviceId}
							</Text>
							<Text style={[styles.tableCell, { flex: 1, paddingLeft: 5, paddingRight: 5 }]}>
								{serviceOriginal?.vehicle.fleetNumber}
							</Text>
							<Text style={[styles.tableCell, { flex: 3, paddingLeft: 5, paddingRight: 5 }]}>
								{serviceOriginal?.pax ?? 0}
							</Text>
						</View>
					</View>
				</View>

				{/* Kms Section */}
				<View style={styles.section}>
					<View style={[styles.table, { marginBottom: 0 }]}>
						{/* Table Header */}
						<View
							style={styles.tableRow}
							wrap={false}
						>
							<Text style={[styles.tableHeader, { flex: 8.5 }]}>Registo de Kms Efetuados</Text>
							<Text style={[styles.tableHeader, { flex: 1 }]}>Kms Início</Text>
							<Text style={[styles.tableHeader, { flex: 1 }]}>Kms Fim</Text>
							<Text style={[styles.tableHeader, { flex: 1.5 }]}>Carga/Vazio</Text>
						</View>
						{/* Table Rows */}
						{kms?.map((item, i) => (
							<View
								style={styles.tableRow}
								wrap={false}
								key={item.kmsId ?? i} // Use kmsId as key if available, otherwise fallback to index
							>
								<Text style={[styles.tableCell, { flex: 8.5, paddingLeft: 7, paddingRight: 7 }]}>Registo {i + 1}</Text>
								<Text style={[styles.tableCell, { flex: 1, paddingLeft: 5, paddingRight: 5 }]}>{item.kmsIni}</Text>
								<Text style={[styles.tableCell, { flex: 1, paddingLeft: 5, paddingRight: 5 }]}>{item.kmsEnd}</Text>
								<Text style={[styles.tableCell, { flex: 1.5, paddingLeft: 5, paddingRight: 5 }]}>
									{item.inEmpty ? 'Vazio' : 'Carga'}
								</Text>
							</View>
						))}
					</View>
					{/* Table Footer (Totals) */}
					<View style={styles.table}>
						{/* Table Header */}
						<View
							style={styles.tableRow}
							wrap={false}
						>
							<Text style={[styles.tableHeader, { flex: 8.5 }]} /> {/* Vazio para alinhar com os dados abaixo */}
							<Text style={[styles.tableHeader, { flex: 1 }]}>Kms Carga</Text>
							<Text style={[styles.tableHeader, { flex: 1 }]}>Kms Vazio</Text>
							<Text style={[styles.tableHeader, { flex: 1.5 }]}>Total</Text>
						</View>
						{/* Table Rows */}
						<View
							style={styles.tableRow}
							wrap={false}
						>
							<Text style={[styles.tableCell, { flex: 8.5, fontWeight: 700, paddingLeft: 7, paddingRight: 7 }]}>
								Totais de Kms
							</Text>
							<Text style={[styles.tableCell, { flex: 1, paddingLeft: 5, paddingRight: 5 }]}>
								{service &&
									(timesheetPage?.isSubmitted
										? calculateKmsByMode(service.serviceExecution as ServiceExecution, false)
										: calculateKmsByMode(service.service as ServiceOriginal, false))}
							</Text>
							<Text style={[styles.tableCell, { flex: 1, paddingLeft: 5, paddingRight: 5 }]}>
								{service &&
									(timesheetPage?.isSubmitted
										? calculateKmsByMode(service.serviceExecution as ServiceExecution, true)
										: calculateKmsByMode(service.service as ServiceOriginal, true))}
							</Text>
							<Text style={[styles.tableCell, { flex: 1.5, paddingLeft: 5, paddingRight: 5 }]}>
								{service &&
									(timesheetPage?.isSubmitted
										? calculateKmsTotal(service.serviceExecution as ServiceExecution)
										: calculateKmsTotal(service.service as ServiceOriginal))}
							</Text>
						</View>
					</View>
				</View>

				{/* Additional Notes Section */}
				<View
					style={styles.section}
					wrap={false}
				>
					<View style={[styles.table]}>
						<View style={styles.tableRow}>
							<Text style={[styles.tableCell, { flex: 2, fontWeight: 700, paddingLeft: 5 }]}>Notas Adicionais:</Text>{' '}
							<Text style={[styles.tableCell, { flex: 10 }]}>{coments || 'Sem notas adicionais!'}</Text>
						</View>
					</View>
				</View>

				{/* Paginated Footer */}
				<View
					style={[
						styles.footer,
						{
							width: '90%',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							alignItems: 'center',
						},
					]}
				>
					<Image
						src={mapImageCompanyUrlTo[storedCompany as keyof typeof mapImageCompanyUrlTo]}
						style={{ height: 20, width: 60, marginBottom: 5 }}
					/>
					<Text
						render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`}
						fixed
					/>
				</View>
			</Page>
		</Document>
	)
}

export const usePrintServiceReportData = (serviceId: number) => {
	const isTimesheetApproved = useIsTimesheetApproved()
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)
	const timesheetApprovalDetailsAccept = useSelector(
		(state: TimesheetStoreState) => state.timesheetApprovalDetailsAccept,
	)

	const serviceOriginal: ServiceOriginal = timesheetPage?.services.find(
		service => service.service?.serviceId === serviceId,
	)?.service as ServiceOriginal

	const serviceExecution: ServiceExecution = timesheetPage?.services.find(
		service => service.serviceExecution?.serviceId === serviceId,
	)?.serviceExecution as ServiceExecution

	const serviceApproval: ServiceExecutionUpdateRequest = timesheetApprovalDetailsAccept?.services.find(
		service => service?.serviceId === serviceId,
	) as ServiceExecutionUpdateRequest

	return {
		timesheetPage,
		serviceOriginal,
		serviceExecution,
		serviceApproval,
		isTimesheetApproved,
	}
}
